// src/pages/TestPage/index.tsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Brain, X, Trophy, Clock } from 'lucide-react';
import { useCategory, useRecordActivity, useActivityTimer, useHierarchicalProgress, useUserActivities } from '../../hooks';
import { Card, LoadingSpinner, Button, Badge, Alert } from '../../components/common';
import { categoryService } from '../../services/api';
import {
  ActivityType,
  AnyQuestionUI,
  DragDropQuestionUI,
  QuizQuestionUI,
  QuizAnswerUI,
  DragDropOptions,
  QuizOptionsRoot,
  QuizSubQuestion,
} from '../../types';

import QuickUserActions from '@/components/users/QuickUserActions';

/** ================= Helpers ================= */
function safeJson<T = any>(val: any, fallback: T): T {
  if (val == null) return fallback;
  if (typeof val === 'string') { try { return JSON.parse(val) as T; } catch { return fallback; } }
  return val as T;
}
function parseCorrectAnswer(raw: any): string | string[] | undefined {
  if (raw == null) return undefined;
  if (Array.isArray(raw)) return raw.map((x) => String(x).trim());
  if (typeof raw === 'string') {
    const t = raw.trim();
    if ((t.startsWith('[') && t.endsWith(']')) || (t.startsWith('{') && t.endsWith('}'))) {
      try {
        const parsed = JSON.parse(t);
        return Array.isArray(parsed) ? parsed.map((x: any) => String(x).trim()) : String(parsed).trim();
      } catch {}
    }
    if (t.includes(',') || t.includes(';')) {
      return t.split(/[,;]+/).map((s) => s.trim()).filter(Boolean);
    }
    return t;
  }
  return String(raw).trim();
}
function pickCorrectAnswer(src: any): string | string[] | undefined {
  return parseCorrectAnswer(src?.correct_answer ?? src?.correctAnswer);
}

/** DB sub-pregunta para 'questions' (tipada para el normalizador) */
type DBSubQ = QuizSubQuestion;

const PASS_PERCENT = 0.7; // 70%
const TEST_TIME = 300;    // 5 min

// Para drop táctil
type TouchDrop = { kind: 'touch'; itemId: string };

/** ================= Evaluación estricta DnD ================= */
function evaluateDnDQuestion(
  q: DragDropQuestionUI,
  userDrop: Record<string, string[]>
): {
  zones: Array<{
    zoneId: string;
    userCount: number;
    needCount: number;
    extraCount: number;
    missingCount: number;
    isCorrect: boolean;
  }>;
  isQuestionCorrect: boolean;
} {
  const zones = q.options.dropZones.map((z) => {
    const userSet = new Set((userDrop[z.id] || []).map(String));
    const correctSet = new Set(z.correctItems.map(String));
    const onlyExpected = [...userSet].every((id) => correctSet.has(id));
    const allPresent  = [...correctSet].every((id) => userSet.has(id));
    const extraCount   = [...userSet].filter((id) => !correctSet.has(id)).length;
    const missingCount = [...correctSet].filter((id) => !userSet.has(id)).length;
    const isCorrect = onlyExpected && allPresent;
    return { zoneId: z.id, userCount: userSet.size, needCount: correctSet.size, extraCount, missingCount, isCorrect };
  });
  const isQuestionCorrect = zones.every((z) => z.isCorrect);
  return { zones, isQuestionCorrect };
}

const TestPage: React.FC = () => {
  const { categoryId } = useParams<{ categoryId: string }>();
  const { data: categoryData, isLoading } = useCategory(categoryId!);
  const recordActivity = useRecordActivity();
  const { getElapsedSeconds } = useActivityTimer();

  const [testStarted, setTestStarted] = useState(false);
  const [testCompleted, setTestCompleted] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [timeLeft, setTimeLeft] = useState(TEST_TIME);
  const [score, setScore] = useState(0);

  // Estado específico por tipo
  const [answersDND, setAnswersDND] = useState<Record<string, Record<string, string[]>>>({});
  const [draggedItem, setDraggedItem] = useState<string | null>(null);

  const [quizFeedback, setQuizFeedback] = useState<'ok' | 'bad' | null>(null);
  const [quizMultiSel, setQuizMultiSel] = useState<string[]>([]);
  const [navLocked, setNavLocked] = useState(false);

  const correctQuizAnswersRef = useRef<Set<string>>(new Set());
  const [isFinishing, setIsFinishing] = useState(false);
  const activityRecorded = useRef(false);

  const [questions, setQuestions] = useState<AnyQuestionUI[]>([]);
  const category = categoryData?.category;

  const { data: activitiesData } = useUserActivities({ categoryId: categoryId! });
    
      const progressActivity = useHierarchicalProgress(
        categoryData?.category?.subcategories || [],
        activitiesData?.users || []
      );

  /** ====== Desktop drag image sólido ====== */
  const dragPreviewRef = useRef<HTMLDivElement | null>(null);
  const createDesktopDragPreview = (text: string) => {
    const el = document.createElement('div');
    el.className = 'p-3 rounded-lg border bg-white shadow-xl text-brinsa-azul font-medium';
    el.style.position = 'absolute';
    el.style.top = '-9999px';
    el.style.left = '-9999px';
    el.style.zIndex = '9999';
    el.style.pointerEvents = 'none';
    el.textContent = text;
    document.body.appendChild(el);
    dragPreviewRef.current = el;
    return el;
  };
  const cleanupDesktopDragPreview = () => {
    if (dragPreviewRef.current) {
      dragPreviewRef.current.remove();
      dragPreviewRef.current = null;
    }
  };

  /** 1) Cargar preguntas desde DB y expandir sub-preguntas si aplica */
  useEffect(() => {
    const load = async () => {
      if (!categoryId) return;
      try {
        const resp = await categoryService.getTestQuestions(categoryId);
        const rows = Array.isArray(resp) ? resp : (resp?.questions ?? []);
        const expanded: AnyQuestionUI[] = rows.flatMap(expandFromDBRow);
        setQuestions(expanded);
      } catch (err) {
        console.error('Error cargando preguntas:', err);
      }
    };
    load();
  }, [categoryId]);

  /** 2) Timer */
  useEffect(() => {
    if (!testStarted || testCompleted) return;
    if (timeLeft <= 0) {
      finishTest();
      return;
    }
    const t = setInterval(() => setTimeLeft((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, [testStarted, testCompleted, timeLeft]);

  /** 3) Reset al cambiar de pregunta */
  useEffect(() => {
    setQuizFeedback(null);
    setQuizMultiSel([]);
    setDraggedItem(null);
  }, [currentQuestionIndex]);

  const startTest = () => {
    setTestStarted(true);
    setTestCompleted(false);
    setCurrentQuestionIndex(0);
    setTimeLeft(TEST_TIME);
    setScore(0);
    setAnswersDND({});
    setDraggedItem(null);
    setQuizFeedback(null);
    setQuizMultiSel([]);
    setNavLocked(false);
    correctQuizAnswersRef.current = new Set();
    activityRecorded.current = false;
  };

  const resetTest = () => {
    setTestStarted(false);
    setTestCompleted(false);
    setCurrentQuestionIndex(0);
    setTimeLeft(TEST_TIME);
    setScore(0);
    setAnswersDND({});
    setDraggedItem(null);
    setQuizFeedback(null);
    setQuizMultiSel([]);
    setNavLocked(false);
    setIsFinishing(false);
    correctQuizAnswersRef.current = new Set();
    activityRecorded.current = false;
  };

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const currentQuestion = questions[currentQuestionIndex];
  const progress = questions.length ? ((currentQuestionIndex + 1) / questions.length) * 100 : 0;

  /** ------- Lógica DND -------- */
  const handleDragStart = (e: React.DragEvent, itemId: string) => {
    try { e.dataTransfer.setData('text/plain', itemId); } catch {}
    e.dataTransfer.effectAllowed = 'move';
    setDraggedItem(itemId);

    if (currentQuestion?.type === 'drag_drop') {
      const it = currentQuestion.options.items.find(i => i.id === itemId);
      if (it) {
        try {
          const preview = createDesktopDragPreview(it.text);
          e.dataTransfer.setDragImage(preview, 10, 10);
        } catch {}
      }
    }
  };
  const handleDragEnd = () => { cleanupDesktopDragPreview(); };

  // Desktop o táctil
  const handleDrop = (e: React.DragEvent | TouchDrop, dropZoneId: string) => {
    if (!currentQuestion || currentQuestion.type !== 'drag_drop') return;

    let itemId: string | null = null;
    if ('kind' in e && e.kind === 'touch') {
      itemId = e.itemId;
    } else {
      const evt = e as React.DragEvent;
      evt.preventDefault();
      try { evt.dataTransfer.dropEffect = 'move'; } catch {}
      itemId = draggedItem || evt.dataTransfer.getData('text/plain');
      cleanupDesktopDragPreview();
    }
    if (!itemId) return;

    const qid = currentQuestion.id;
    const prev = answersDND[qid] || {};

    // remover item de cualquier zona
    const cleaned: Record<string, string[]> = Object.fromEntries(
      Object.entries(prev).map(([k, arr]) => [k, arr.filter((x) => x !== itemId)])
    );
    // agregar a la zona objetivo
    cleaned[dropZoneId] = [...(cleaned[dropZoneId] || []), itemId];

    setAnswersDND({ ...answersDND, [qid]: cleaned });
    setDraggedItem(null);
  };
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    try { e.dataTransfer.dropEffect = 'move'; } catch {}
  };

  /** ------- Lógica QUIZ -------- */
  const isQuizMulti = useMemo(() => {
    if (!currentQuestion || currentQuestion.type !== 'questions') return false;
    return (currentQuestion.options.mode || 'single') === 'multi';
  }, [currentQuestion]);

  const handleQuizSingleClick = (ans: QuizAnswerUI) => {
    if (!currentQuestion || currentQuestion.type !== 'questions') return;
    const ok = isQuizAnswerCorrect(currentQuestion, [ans.id]);
    setQuizFeedback(ok ? 'ok' : 'bad');
    setNavLocked(true);
    if (ok) {
      setScore((s) => s + 1);
      correctQuizAnswersRef.current = new Set([...correctQuizAnswersRef.current, currentQuestion.id]);
    }
    setTimeout(() => {
      setQuizFeedback(null);
      setNavLocked(false);
      proceedAfterQuestion();
    }, ok ? 500 : 700);
  };

  const toggleQuizMulti = (id: string) => {
    setQuizMultiSel((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  const confirmQuizMulti = () => {
    if (!currentQuestion || currentQuestion.type !== 'questions') return;
    const ok = isQuizAnswerCorrect(currentQuestion, quizMultiSel);
    setQuizFeedback(ok ? 'ok' : 'bad');
    setNavLocked(true);
    if (ok) {
      setScore((s) => s + 1);
      correctQuizAnswersRef.current = new Set([...correctQuizAnswersRef.current, currentQuestion.id]);
    }
    setTimeout(() => {
      setQuizFeedback(null);
      setNavLocked(false);
      if (ok) setQuizMultiSel([]);
      proceedAfterQuestion();
    }, ok ? 500 : 700);
  };
  const clearQuizMulti = () => setQuizMultiSel([]);

  /** ---- Finalizar (calcular score drag&drop + quiz) ---- */
  const finishTest = async () => {
    if (activityRecorded.current || testCompleted) return;

    // 1. DnD: 1 punto solo si TODAS las zonas de esa sub-pregunta están correctas
    let dndScore = 0;
    for (const q of questions) {
      if (q.type !== 'drag_drop') continue;
      const drop = answersDND[q.id] || {};
      const { isQuestionCorrect } = evaluateDnDQuestion(q, drop);
      dndScore += isQuestionCorrect ? 1 : 0;
    }

    // 2. Quiz score vía ref
    const quizScore = correctQuizAnswersRef.current.size;

    // 3. Totales
    const totalQuestions = questions.length;
    const timeUsed = TEST_TIME - timeLeft;
    const totalScore = quizScore + dndScore;
    const passed = (totalScore / totalQuestions) >= PASS_PERCENT;

    activityRecorded.current = true;
    setScore(totalScore);
    setTestCompleted(true);

    try {
      await recordActivity.mutateAsync({
        categoryId: categoryId!,
        activityType: ActivityType.TEST,
        completed: passed,
        timeSpent: getElapsedSeconds(),
        metadata: {
          score: totalScore,
          totalQuestions,
          timeUsed,
          passed,
          quizScore,
          dndScore,
          correctQuizAnswers: Array.from(correctQuizAnswersRef.current)
        }
      });
    } catch (error) {
      activityRecorded.current = false;
    }
  };

  const proceedAfterQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex((i) => i + 1);
    } else {
      finishTest();
    }
  };

  /** UI */
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }
  if (!category) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-brinsa-azul mb-4">Categoría no encontrada</h2>
        <Link to="/home">
          <Button variant="outline">Volver a categorías</Button>
        </Link>
      </div>
    );
  }
  if (questions.length === 0) {
    return (
      <div className="text-center py-12">
        <Brain className="w-16 h-16 text-brinsa-azulOscuro/50 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-brinsa-azul mb-2">No hay preguntas disponibles</h3>
        <p className="text-brinsa-azulOscuro/80 mb-6">Esta categoría no tiene preguntas de test configuradas aún.</p>
        <Link to={`/category/${categoryId}`}>
          <Button variant="outline">Volver a la categoría</Button>
        </Link>
      </div>
    );
  }

  const isKnownType = currentQuestion?.type === 'drag_drop' || currentQuestion?.type === 'questions';
  const isDnd = currentQuestion?.type === 'drag_drop';
  const hasAnyDrop = isDnd
    ? Object.values(answersDND[currentQuestion.id] || {}).some((arr) => (arr?.length ?? 0) > 0)
    : true;

  return (
    <div className="space-y-6">
      {/* Nav */}
      <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }}>
        <Link
          to={`/category/${categoryId}`}
          className="inline-flex items-center text-brinsa-azulOscuro/80 hover:text-brinsa-azul transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver a {category.name}
        </Link>
      </motion.div>

      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-center">
        <h1 className="text-3xl font-bold text-brinsa-azul mb-4">Prueba de Conocimientos: {category.name}</h1>
        <p className="text-lg text-brinsa-azulOscuro/80">Evalúa lo que has aprendido con actividades interactivas</p>
      </motion.div>

      <AnimatePresence mode="wait">
        {!testStarted ? (
          // START
          <motion.div key="start" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.6 }}>
            <Card className="max-w-2xl mx-auto">
              <div className="p-8 text-center">
                <div className="w-20 h-20 bg-brinsa-verde/15 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Brain className="w-10 h-10 text-brinsa-verde" />
                </div>
                <h2 className="text-2xl font-bold text-brinsa-azul mb-4">¿Listo para la prueba?</h2>
                <div className="bg-brinsa-celeste/10 border border-brinsa-celeste/30 p-6 rounded-lg mb-6">
                  <h3 className="font-semibold text-brinsa-azul mb-3">Información del test:</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div className="text-center">
                      <div className="font-medium text-brinsa-azul">{questions.length}</div>
                      <div className="text-brinsa-azulOscuro/80">Preguntas</div>
                    </div>
                    <div className="text-center">
                      <div className="font-medium text-brinsa-azul">{Math.floor(TEST_TIME/60)} min</div>
                      <div className="text-brinsa-azulOscuro/80">Tiempo límite</div>
                    </div>
                    <div className="text-center">
                      <div className="font-medium text-brinsa-azul">{Math.round(PASS_PERCENT*100)}%</div>
                      <div className="text-brinsa-azulOscuro/80">Para aprobar</div>
                    </div>
                  </div>
                </div>
                <Button size="lg" onClick={startTest} leftIcon={<Brain className="w-5 h-5" />}>Comenzar Prueba</Button>
              </div>
            </Card>
          </motion.div>
        ) : testCompleted ? (
          // RESULTS
          <motion.div key="results" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.6 }}>
            <Card className="max-w-2xl mx-auto">
              <div className="p-8 text-center">
                <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 ${
                  score >= Math.ceil(questions.length * PASS_PERCENT) ? 'bg-brinsa-verde/15' : 'bg-brinsa-rojo/15'
                }`}>
                  {score >= Math.ceil(questions.length * PASS_PERCENT)
                    ? <Trophy className="w-10 h-10 text-brinsa-verde" />
                    : <X className="w-10 h-10 text-brinsa-rojo" />
                  }
                </div>
                <h2 className="text-2xl font-bold text-brinsa-azul mb-4">
                  {score >= Math.ceil(questions.length * PASS_PERCENT) ? '¡Felicitaciones!' : 'Sigue intentando'}
                </h2>
                <div className="bg-slate-50 p-6 rounded-lg mb-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-brinsa-azulClaro">{score}</div>
                      <div className="text-sm text-brinsa-azulOscuro/80">Puntos obtenidos</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-brinsa-azulClaro">
                        {Math.round((score / questions.length) * 100)}%
                      </div>
                      <div className="text-sm text-brinsa-azulOscuro/80">Porcentaje</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-brinsa-azulClaro">{formatTime(TEST_TIME - timeLeft)}</div>
                      <div className="text-sm text-brinsa-azulOscuro/80">Tiempo usado</div>
                    </div>
                  </div>
                </div>

                {score >= Math.ceil(questions.length * PASS_PERCENT) ? (
                  <Alert type="success" title="¡Prueba aprobada!" message="Tu certificado estará disponible cuando completes todas las actividades."/>
                ) : (
                  <Alert type="warning" title="Prueba no aprobada" message="Necesitas al menos 70% para aprobar. Revisa el contenido y vuelve a intentarlo."/>
                )}

                <div className="flex justify-center space-x-4 mt-6">
                  <Button variant="outline" onClick={resetTest}>Intentar de nuevo</Button>
                  <Link to={`/category/${categoryId}`}><Button>Continuar</Button></Link>
                </div>
              </div>
            </Card>
          </motion.div>
        ) : (
          // QUESTION
          <motion.div key="question" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.6 }}>
            {/* Barra de progreso & timer */}
            <Card>
              <div className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-4">
                    <span className="text-sm font-medium text-brinsa-azulOscuro/80">Pregunta {currentQuestionIndex + 1} de {questions.length}</span>
                    <div className="flex items-center space-x-2 text-sm text-brinsa-azulOscuro/80">
                      <Clock className="w-4 h-4"/>
                      <span className={timeLeft < 60 ? 'text-brinsa-rojo font-medium' : ''}>{formatTime(timeLeft)}</span>
                    </div>
                  </div>
                  <Badge variant="info" size="sm">{Math.round(progress)}% completado</Badge>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-2">
                  <div className="bg-brinsa-azulClaro h-2 rounded-full transition-all duration-300" style={{ width: `${progress}%` }}/>
                </div>
              </div>
            </Card>

            {/* Enunciado + cuerpo según tipo */}
            <Card>
              <div className="p-6">
                {!isKnownType ? (
                  <div className="text-center text-brinsa-rojo">Pregunta con tipo desconocido.</div>
                ) : (
                  <>
                    <h3 className="text-xl font-semibold text-brinsa-azul mb-2">{currentQuestion.question}</h3>

                    {currentQuestion.type === 'drag_drop' ? (
                      <DragDropUI
                        q={currentQuestion}
                        userDrop={answersDND[currentQuestion.id] || {}}
                        onDragStart={handleDragStart}
                        onDrop={handleDrop}
                        onDragOver={handleDragOver}
                        onDragEnd={handleDragEnd}
                      />
                    ) : (
                      <QuizUI
                        q={currentQuestion}
                        feedback={quizFeedback}
                        multiSel={quizMultiSel}
                        isMulti={isQuizMulti}
                        onSingleClick={handleQuizSingleClick}
                        onToggleMulti={toggleQuizMulti}
                        onConfirmMulti={confirmQuizMulti}
                        onClearMulti={clearQuizMulti}
                      />
                    )}

                    {/* Navegación */}
                    <div className="flex justify-between mt-8">
                      <Button
                        variant="outline"
                        onClick={() => setCurrentQuestionIndex((i) => Math.max(0, i - 1))}
                        disabled={currentQuestionIndex === 0 || navLocked}
                      >
                        Anterior
                      </Button>
                      <Button
                        onClick={() => {
                          if (currentQuestionIndex === questions.length - 1) finishTest();
                          else setCurrentQuestionIndex((i) => i + 1);
                        }}
                        disabled={!hasAnyDrop || navLocked || isFinishing}
                      >
                        {currentQuestionIndex === questions.length - 1 ? 'Finalizar' : 'Siguiente'}
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </Card>
          </motion.div>
        )}
        <QuickUserActions progress={progressActivity}/>
      </AnimatePresence>
    </div>
  );
};

export default TestPage;

/** ================= Componentes UI auxiliares ================= */

function DragDropUI({
  q,
  userDrop,
  onDragStart,
  onDrop,
  onDragOver,
  onDragEnd,
}: {
  q: DragDropQuestionUI;
  userDrop: Record<string, string[]>;
  onDragStart: (e: React.DragEvent, itemId: string) => void;
  onDrop: (e: React.DragEvent | { kind: 'touch'; itemId: string }, dropZoneId: string) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragEnd: (e: React.DragEvent) => void;
}) {
  const used = new Set(Object.values(userDrop).flat());

  // ====== Touch fallback ======
  const [touchDraggingId, setTouchDraggingId] = React.useState<string | null>(null);
  const [touchPos, setTouchPos] = React.useState<{ x: number; y: number } | null>(null);
  const touchMoveRef = React.useRef<(e: TouchEvent) => void>();
  const touchEndRef = React.useRef<(e: TouchEvent) => void>();

  const isTouchDevice =
    typeof window !== 'undefined' &&
    (('ontouchstart' in window) || (navigator as any).maxTouchPoints > 0);

  const findDropZoneAt = React.useCallback((x: number, y: number): string | null => {
    const el = document.elementFromPoint(x, y);
    if (!el) return null;
    let node: HTMLElement | null = el as HTMLElement;
    while (node) {
      if (node.dataset && node.dataset.dropzone === 'true' && node.dataset.zoneId) {
        return node.dataset.zoneId;
      }
      node = node.parentElement;
    }
    return null;
  }, []);

  const cancelTouchDnD = React.useCallback(() => {
    setTouchDraggingId(null);
    setTouchPos(null);
    if (touchMoveRef.current) document.removeEventListener('touchmove', touchMoveRef.current);
    if (touchEndRef.current) document.removeEventListener('touchend', touchEndRef.current);
    touchMoveRef.current = undefined;
    touchEndRef.current = undefined;
  }, []);

  const handleTouchStart = React.useCallback((e: React.TouchEvent, itemId: string) => {
    if (used.has(itemId)) return;
    setTouchDraggingId(itemId);
    const t = e.touches[0];
    setTouchPos({ x: t.clientX, y: t.clientY });
    const currentId = itemId;

    touchMoveRef.current = (ev: TouchEvent) => {
      const tt = ev.touches[0];
      if (!tt) return;
      ev.preventDefault();
      setTouchPos({ x: tt.clientX, y: tt.clientY });
    };
    touchEndRef.current = (ev: TouchEvent) => {
      const tt = ev.changedTouches[0];
      if (!tt) { cancelTouchDnD(); return; }
      const z = findDropZoneAt(tt.clientX, tt.clientY);
      if (z) onDrop({ kind: 'touch', itemId: currentId } as const, z);
      cancelTouchDnD();
    };

    document.addEventListener('touchmove', touchMoveRef.current, { passive: false });
    document.addEventListener('touchend', touchEndRef.current, { passive: false });
  }, [used, cancelTouchDnD, findDropZoneAt, onDrop]);

  /** ====== Pointer Events fallback táctil robusto (no interfiere con mouse nativo) ====== */
  const handlePointerTouchStart = React.useCallback((e: React.PointerEvent, itemId: string) => {
    // Solo usar este flujo cuando el puntero sea táctil y el item no esté usado
    if (e.pointerType !== 'touch' || used.has(itemId)) return;

    try { (e.target as Element).setPointerCapture?.(e.pointerId); } catch {}
    setTouchDraggingId(itemId);
    setTouchPos({ x: e.clientX, y: e.clientY });
    const currentId = itemId;

    const move = (ev: PointerEvent) => {
      if (ev.pointerType !== 'touch') return;
      try { ev.preventDefault(); } catch {}
      setTouchPos({ x: ev.clientX, y: ev.clientY });
    };
    const up = (ev: PointerEvent) => {
      if (ev.pointerType !== 'touch') return;
      const z = findDropZoneAt(ev.clientX, ev.clientY);
      if (z) onDrop({ kind: 'touch', itemId: currentId } as const, z);

      document.removeEventListener('pointermove', move, { capture: true } as any);
      document.removeEventListener('pointerup', up, { capture: true } as any);
      setTouchDraggingId(null);
      setTouchPos(null);
    };

    document.addEventListener('pointermove', move, { passive: false, capture: true } as any);
    document.addEventListener('pointerup', up, { passive: false, capture: true } as any);
  }, [used, findDropZoneAt, onDrop]);

  const renderTouchGhost = () => {
    if (!touchDraggingId || !touchPos) return null;
    const it = q.options.items.find(i => i.id === touchDraggingId);
    if (!it) return null;
    return (
      <div
        style={{ position: 'fixed', left: touchPos.x + 8, top: touchPos.y + 8, transform: 'translate(-50%, -50%)', pointerEvents: 'none', zIndex: 9999 }}
        className="p-3 rounded-lg border bg-white shadow-xl select-none"
      >
        {it.text}
      </div>
    );
  };

  // Evaluación para feedback inmediato
  const evalRes = evaluateDnDQuestion(q, userDrop);
  const zoneMap = new Map(evalRes.zones.map(z => [z.zoneId, z]));

  const webkitStyle: React.CSSProperties & { [key: string]: any } = {
    userSelect: 'none',
    WebkitUserDrag: 'element',
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" style={{ touchAction: 'none' }}>
      {/* Items */}
      <div>
        <h4 className="font-medium text-brinsa-azul mb-4">Arrastra los elementos:</h4>
        <div className="space-y-3">
          {q.options.items.map((item) => {
            const isUsed = used.has(item.id);
            // SIEMPRE habilitamos draggable para mouse; el fallback táctil va por Pointer Events
            const draggableDesktop = !isUsed;
            return (
              <div
                key={item.id}
                draggable={draggableDesktop}
                onPointerDown={(e) => {
                  // Fallback táctil robusto sin afectar mouse nativo
                  if ((e as React.PointerEvent).pointerType === 'touch') {
                    handlePointerTouchStart(e as React.PointerEvent, item.id);
                  }
                }}
                onDragStart={(e) => { onDragStart(e, item.id); }}
                onDragEnd={(e) => { onDragEnd(e); }}
                onTouchStart={(e) => { if (isTouchDevice) handleTouchStart(e, item.id); }} /* mantiene tu fallback touch previo */
                className={`p-3 rounded-lg border transition-colors select-none ${
                  isUsed
                    ? 'border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed'
                    : 'border-brinsa-celeste/60 bg-brinsa-celeste/10 hover:bg-brinsa-celeste/20 text-brinsa-azul cursor-move'
                } dnd-item`}
                style={webkitStyle} /* Safari/WebKit: mejora DnD nativo */
              >
                {item.text}
              </div>
            );
          })}
        </div>
      </div>

      {/* Zonas */}
      <div>
        <h4 className="font-medium text-brinsa-azul mb-4">Suelta aquí:</h4>
        <div className="space-y-4">
          {q.options.dropZones.map((zone) => {
            const zoneItems = userDrop[zone.id] || [];
            const zres = zoneMap.get(zone.id);
            const hasAny = zoneItems.length > 0;

            const base = "min-h-[100px] p-4 rounded-lg transition-colors border-2 border-dashed bg-slate-50";
            const cls = zres?.isCorrect
              ? "border-green-500/70"
              : hasAny
                ? "border-red-500/70"
                : "border-slate-300 hover:border-brinsa-celeste/60";

            return (
              <div
                key={zone.id}
                data-dropzone="true"
                data-zone-id={zone.id}
                onDrop={(e) => onDrop(e, zone.id)}
                onDragEnter={(e) => { e.preventDefault(); }}
                onDragOver={(e) => { e.preventDefault(); try { e.dataTransfer.dropEffect = 'move'; } catch {} }}
                className={`${base} ${cls}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <h5 className="font-medium text-brinsa-azul">{zone.label}</h5>
                  {hasAny && zres && (
                    <span className={`text-xs font-semibold ${zres.isCorrect ? "text-green-600" : "text-red-600"}`}>
                      {zres.isCorrect
                        ? "✓ Completo"
                        : (() => {
                            const parts = [];
                            if (zres.missingCount > 0) parts.push(`faltan ${zres.missingCount}`);
                            if (zres.extraCount > 0) parts.push(`sobran ${zres.extraCount}`);
                            return `✗ ${parts.join(", ") || "incompleto"}`;
                          })()}
                    </span>
                  )}
                </div>

                <div className="space-y-2">
                  {zoneItems.map((itemId) => {
                    const it = q.options.items.find((i) => i.id === itemId);
                    const isExtra =
                      zres && it ? !q.options.dropZones
                        .find(dz => dz.id === zone.id)!
                        .correctItems.map(String)
                        .includes(String(it.id))
                      : false;

                    return (
                      <div
                        key={itemId}
                        className={[
                          "p-2 rounded text-sm border",
                          isExtra
                            ? "bg-red-50 border-red-300 text-red-700"
                            : "bg-brinsa-verde/15 border-brinsa-verde/40 text-brinsa-verde",
                        ].join(" ")}
                      >
                        {it?.text}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {renderTouchGhost()}
    </div>
  );
}

function QuizUI({
  q,
  feedback,
  multiSel,
  isMulti,
  onSingleClick,
  onToggleMulti,
  onConfirmMulti,
  onClearMulti,
}: {
  q: QuizQuestionUI;
  feedback: 'ok' | 'bad' | null;
  multiSel: string[];
  isMulti: boolean;
  onSingleClick: (a: QuizAnswerUI) => void;
  onToggleMulti: (id: string) => void;
  onConfirmMulti: () => void;
  onClearMulti: () => void;
}) {
  return (
    <div>
      <div className={`mt-2 grid gap-3 ${q.options.answers?.length > 2 ? 'grid-cols-2' : 'grid-cols-1'} sm:grid-cols-2`}>
        {q.options.answers.map((a) => (
          <button
            key={a.id}
            onClick={() => (isMulti ? onToggleMulti(a.id) : onSingleClick(a))}
            className={[
              "rounded-2xl border-2 border-dotted px-4 py-3 text-[#001689] font-bold transition",
              isMulti && multiSel.includes(a.id) ? "bg-[#18f1d8]/40 border-[#001689]" : "border-[#001689]/80 hover:bg-[#c6d1da]/30"
            ].join(' ')}
          >
            {a.img && <img src={a.img} alt={a.text} className="mx-auto mb-2 h-16 object-contain" />}
            <span className="block text-sm">{a.text}</span>
          </button>
        ))}
      </div>

      {isMulti && (
        <div className="mt-5 flex items-center justify-center gap-3">
          <button onClick={onClearMulti} className="px-4 py-2 rounded-xl border border-[#001689] text-[#001689]">
            Limpiar selección
          </button>
          <button onClick={onConfirmMulti} className="px-5 py-2 rounded-xl bg-[#00b93d] text-white font-bold">
            Confirmar
          </button>
        </div>
      )}

      {feedback && (
        <div className={`mt-4 text-center font-bold ${feedback === 'ok' ? 'text-brinsa-verde' : 'text-brinsa-rojo'}`}>
          {feedback === 'ok' ? '¡Correcto!' : 'Incorrecto, intenta de nuevo.'}
        </div>
      )}
    </div>
  );
}

/** ================= Normalización DB -> UI ================= */
function expandFromDBRow(dbRow: any): AnyQuestionUI[] {
  const rawType = (dbRow.question_type ?? dbRow.questionType) as 'drag_drop' | 'questions' | undefined;
  const type: 'drag_drop' | 'questions' = rawType === 'drag_drop' || rawType === 'questions' ? rawType : 'questions';
  const points = dbRow.points ?? 1;

  const optAny = safeJson<any>(dbRow.options, {} as any);
  const rootCorrectAnswer = pickCorrectAnswer(dbRow);

  /** ===== A) DRAG & DROP ===== */
  if (type === 'drag_drop') {
    const opt: DragDropOptions & { questions?: any[] | Record<string, any> } = optAny || {};

    // NUEVO: Soporte para sub-preguntas en drag_drop
    if (opt.questions) {
      const entries = Array.isArray(opt.questions) ? opt.questions : Object.values(opt.questions as Record<string, any>);
      return entries.map((sub: any, idx: number) => {
        const title = String(sub.title ?? dbRow.question);
        const items = (sub.items || []).map((i: any) => ({ id: String(i.id), text: String(i.text) }));
        const dropZones = (sub.dropZones || []).map((z: any) => ({
          id: String(z.id),
          label: String(z.label),
          correctItems: (z.correctItems || []).map((x: any) => String(x)),
        }));
        return {
          id: `${dbRow.id}::${sub.id ?? idx}`,
          question: title,
          type: 'drag_drop',
          points,
          options: { items, dropZones },
        } as DragDropQuestionUI;
      });
    }

    // Formato clásico (una sola dnd)
    return [{
      id: String(dbRow.id),
      question: dbRow.question,
      type: 'drag_drop',
      points,
      options: {
        items: (opt.items || []).map((i: any) => ({ id: String(i.id), text: String(i.text) })),
        dropZones: (opt.dropZones || []).map((z: any) => ({
          id: String(z.id),
          label: String(z.label),
          correctItems: (z.correctItems || []).map((x: any) => String(x)),
        })),
      },
    }];
  }

  /** ===== B) QUESTIONS (single/multi) ===== */
  const opt: QuizOptionsRoot & { questions?: any[] | Record<string, any> } = optAny || {};
  // modo por defecto en el root (se puede sobreescribir por sub si quisieras)
  const rootMode: 'single' | 'multi' = opt.mode === 'multi' ? 'multi' : 'single';

  // NUEVO + EXISTENTE: options.questions -> varias sub-preguntas
  if (opt.questions) {
    const entries = Array.isArray(opt.questions) ? opt.questions : Object.values(opt.questions as Record<string, any>);
    return entries.map((sub: any, idx: number) => {
      const mode: 'single' | 'multi' = sub.mode === 'multi' ? 'multi' : rootMode;
      const answers: QuizAnswerUI[] = (sub.answers || []).map((a: any) => ({
        id: String(a.id),
        text: String(a.text),
        img: a.img ? String(a.img) : undefined,
        correct:
          typeof a.correct === 'boolean'
            ? a.correct
            : (String(a.correct).toLowerCase() === 'true'
                ? true
                : (String(a.correct).toLowerCase() === 'false'
                    ? false
                    : undefined)),
      }));

      const subCorrectAnswer = pickCorrectAnswer(sub);
      if (answers.every(a => typeof a.correct === 'undefined') && subCorrectAnswer) {
        const corrSet = new Set<string>(Array.isArray(subCorrectAnswer) ? subCorrectAnswer.map(String) : [String(subCorrectAnswer)]);
        answers.forEach(a => { a.correct = corrSet.has(a.id); });
      }

      return {
        id: `${dbRow.id}::${sub.id ?? idx}`,
        question: String(sub.title ?? dbRow.question),
        type: 'questions',
        points,
        options: { mode, answers },
        correct_answer: subCorrectAnswer,
      } as QuizQuestionUI;
    });
  }

  // Formato simple (una sola pregunta de selección)
  if (opt.answers) {
    const answers: QuizAnswerUI[] = (opt.answers || []).map((a: any) => ({
      id: String(a.id),
      text: String(a.text),
      img: a.img ? String(a.img) : undefined,
      correct:
        typeof a.correct === 'boolean'
          ? a.correct
          : (String(a.correct).toLowerCase() === 'true'
              ? true
              : (String(a.correct).toLowerCase() === 'false'
                  ? false
                  : undefined)),
    }));

    if (answers.every(a => typeof a.correct === 'undefined') && rootCorrectAnswer) {
      const corrSet = new Set<string>(Array.isArray(rootCorrectAnswer) ? rootCorrectAnswer.map(String) : [String(rootCorrectAnswer)]);
      answers.forEach(a => { a.correct = corrSet.has(a.id); });
    }

    return [{
      id: String(dbRow.id),
      question: dbRow.question,
      type: 'questions',
      points,
      options: { mode: rootMode, answers },
      correct_answer: rootCorrectAnswer,
    } as QuizQuestionUI];
  }

  return [];
}

/** Evalúa si la selección del usuario es correcta en el quiz */
function isQuizAnswerCorrect(q: QuizQuestionUI, selectedIds: string[]): boolean {
  const answers = q.options.answers;
  const selSet = new Set(selectedIds.map((id) => String(id).trim()));
  const withCorrect = answers.some((a) => typeof a.correct === 'boolean');

  if (withCorrect) {
    const correctIds = answers.filter((a) => a.correct).map((a) => String(a.id).trim());
    const correctSet = new Set(correctIds);
    const mode = (q.options as any)?.mode === 'multi' ? 'multi' : 'single';

    if (mode === 'single') {
      return selectedIds.length === 1 && correctIds.some((id) => selSet.has(id));
    }
    const allCorrectSelected = correctIds.every((id) => selSet.has(id));
    const noIncorrectSelected = [...selSet].every((id) => correctSet.has(id));
    return allCorrectSelected && noIncorrectSelected;
  }

  const corr = q.correct_answer;
  const corrArray = Array.isArray(corr) ? corr : [corr];
  const corrSet = new Set(corrArray.filter(Boolean).map((x) => String(x).trim()));
  if (corrSet.size === 0) return false;

  const mode = (q.options as any)?.mode === 'multi' ? 'multi' : 'single';
  if (mode === 'single') {
    return selectedIds.length === 1 && corrArray.some((id) => selSet.has(String(id).trim()));
    }

  const allCorrectSelected = [...corrSet].every((id) => selSet.has(id));
  const noIncorrectSelected = [...selSet].every((id) => corrSet.has(id));
  return allCorrectSelected && noIncorrectSelected;
}
